import { AtsCareerSiteVisibilityEnum } from '@/generated/graphql/types';
import { useNavigate } from '@/shared/modern-router';
import { extractIdFromUrl } from '@/shared/utils/extract-id-from-url';
import ArrowRight from '@crehana/compass.icons.outline/ArrowRight';
import Briefcase from '@crehana/compass.icons.outline/Briefcase';
import { toast } from '@crehana/compass.ui/toaster';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@crehana/compass.ui/tooltip';
import { format } from 'date-fns';
import { useTranslationCareerSiteAdminShared } from '../../../i18n/use-translations';
import type { TVacancyCardProps } from './types';

export const VacancyCard: React.FC<TVacancyCardProps> = ({ row, color }) => {
  const { t } = useTranslationCareerSiteAdminShared();
  const navigate = useNavigate();

  const handleClick = () => {
    if (!row.actions.disabled) {
      const { vacancy_link, type, career_site_id } = row.actions;

      if (type === AtsCareerSiteVisibilityEnum.InternalAccess) {
        // Extract vacancy UID from vacancy_link using robust URL parsing
        const jobId = extractIdFromUrl(vacancy_link);
        if (jobId) {
          navigate.navigateTo({
            path: '/[slug]/ats/career-site/jobs/[jobId]/',
            params: { jobId },
            searchParams: career_site_id
              ? { career_site: career_site_id }
              : undefined,
          });
        } else {
          toast.error(t('FAILED_TO_EXTRACT_JOB_ID_FROM_VACANCY_LINK'));
        }
      } else {
        if (!vacancy_link) {
          toast.error(t('VACANCY_LINK_IS_EMPTY'));
          return;
        }

        // Add career_site_id as query param to the URL if available
        const url = new URL(vacancy_link, window.location.origin);
        if (career_site_id) {
          url.searchParams.set('career_site', career_site_id);
        }
        window.open(url.toString(), '_blank');
      }
    }
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.borderColor = color?.primaryColor || '';
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.borderColor = '';
  };

  return (
    <button
      className="tw-rounded-1 tw-border tw-border-solid tw-border-neutral-light-600 tw-flex tw-flex-col tw-justify-between tw-bg-neutral-light-100"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      disabled={row.actions.disabled}
      type="button"
    >
      <div className="tw-flex tw-p-1.6 tw-pb-0 tw-justify-between tw-items-center">
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-0.8 tw-flex-1 tw-min-w-0">
          <div className="tw-text-start tw-gap-0.8 tw-flex-1 tw-min-w-0">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger className="tw-w-fit">
                  <p className="tw-font-subtitle5 tw-w-fit tw-text-start tw-break-all tw-line-clamp-1">
                    {row.vacancy.title}
                  </p>
                </TooltipTrigger>
                <TooltipContent>
                  <span className="tw-font-body5">{row.vacancy.title}</span>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div
            className="tw-flex tw-items-center tw-gap-0.8 tw-rounded-full tw-justify-center tw-flex-shrink-0"
            style={{ color: color?.primaryColor }}
          >
            <Briefcase size={16} className="tw-text-current" />
            <p className="tw-font-body5 tw-font-semibold tw-text-current">
              {row.hiring_number}
            </p>
          </div>
        </div>
        <div
          style={{ backgroundColor: color?.secondaryColor }}
          className="tw-flex tw-items-center tw-justify-center tw-rounded-[0.6rem] tw-h-4 tw-w-4 tw-flex-shrink-0 tw-ml-2"
        >
          <p style={{ color: color?.primaryColor }}>
            <ArrowRight size={21} className="tw-text-current" />
          </p>
        </div>
      </div>
      <div className="tw-flex tw-px-1.6 tw-pb-1.2">
        <p className="tw-font-body5 tw-h-1.6 tw-text-neutral-light-700">
          {row.area.name}
        </p>
      </div>
      <div className="tw-flex tw-px-1.6 tw-rounded-b-1 tw-items-center tw-justify-start tw-bg-neutral-light-200 tw-h-4.8">
        <p className="tw-font-body5 tw-text-neutral-light-700">
          {t('CLOSED_DATE_CARD', {
            interpolation: {
              date: format(new Date(row.vacancy.open_date), 'dd/MM/yyyy'),
            },
          })}
        </p>
      </div>
    </button>
  );
};
